// syncValidation.js
// Utility to check if data is too old and needs syncing

import { db } from '../db';

// Configuration - adjust these values as needed
// const SYNC_WARNING_DAYS = 7; // Show warning after 7 days
// const SYNC_BLOCK_DAYS = 14;  // Block new data after 14 days

const SYNC_WARNING_DAYS = 3;  // Warning immediately
const SYNC_BLOCK_DAYS = 7; 

/**
 * Check if there's unsynced data older than the specified days
 * @param {number} days - Number of days to check
 * @returns {Promise<{hasOldData: boolean, oldestData: Date|null, dataCount: number}>}
 */
export const checkOldUnsyncedData = async (days = SYNC_WARNING_DAYS) => {
  try {
    
    // Get current date
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
    
    // Check patients table
    const oldPatients = await db.patients
      .where('syncStatus')
      .equals('pending')
      .filter(patient => {
        const createdAt = new Date(patient.createdAt);
        return createdAt < cutoffDate;
      })
      .toArray();
    
    // Check reports table
    const oldReports = await db.reports
      .where('syncStatus')
      .equals('pending')
      .filter(report => {
        const createdAt = new Date(report.createdAt);
        return createdAt < cutoffDate;
      })
      .toArray();
    
    const totalOldData = oldReports.length;
    const hasOldData = totalOldData > 0;
    
    // Find the oldest data
    let oldestData = null;
    if (hasOldData) {
      const allData = [...oldPatients, ...oldReports];
      oldestData = allData.reduce((oldest, current) => {
        const currentDate = new Date(current.createdAt);
        const oldestDate = oldest ? new Date(oldest.createdAt) : new Date();
        return currentDate < oldestDate ? current : oldest;
      });
    }
    
    
    return {
      hasOldData,
      oldestData: oldestData ? new Date(oldestData.createdAt) : null,
      dataCount: totalOldData
    };
  } catch (error) {
    console.error('❌ Error checking old unsynced data:', error);
    return {
      hasOldData: false,
      oldestData: null,
      dataCount: 0
    };
  }
};

/**
 * Check if new data entry should be blocked
 * @returns {Promise<{blocked: boolean, reason: string|null, daysOld: number|null}>}
 */
export const shouldBlockNewData = async () => {
  try {
    const oldDataCheck = await checkOldUnsyncedData(SYNC_BLOCK_DAYS);
    
    if (oldDataCheck.hasOldData) {
      const daysOld = Math.floor(
        (new Date() - oldDataCheck.oldestData) / (24 * 60 * 60 * 1000)
      );
      
      return {
        blocked: true,
        reason: `You have ${oldDataCheck.dataCount} unsynced records that are ${daysOld} days old. Please sync your data before adding new records.`,
        daysOld
      };
    }
    
    return {
      blocked: false,
      reason: null,
      daysOld: null
    };
  } catch (error) {
    console.error('❌ Error checking if new data should be blocked:', error);
    return {
      blocked: false,
      reason: null,
      daysOld: null
    };
  }
};

/**
 * Check if warning should be shown (data is getting old but not blocking yet)
 * @returns {Promise<{showWarning: boolean, message: string|null, daysOld: number|null}>}
 */
export const shouldShowSyncWarning = async () => {
  try {
    const oldDataCheck = await checkOldUnsyncedData(SYNC_WARNING_DAYS);
    
    if (oldDataCheck.hasOldData) {
      const daysOld = Math.floor(
        (new Date() - oldDataCheck.oldestData) / (24 * 60 * 60 * 1000)
      );
      
      return {
        showWarning: true,
        message: `You have ${oldDataCheck.dataCount} unsynced records that are ${daysOld} days old. Consider syncing soon to avoid data entry restrictions.`,
        daysOld
      };
    }
    
    return {
      showWarning: false,
      message: null,
      daysOld: null
    };
  } catch (error) {
    console.error('❌ Error checking if sync warning should be shown:', error);
    return {
      showWarning: false,
      message: null,
      daysOld: null
    };
  }
};

/**
 * Get sync status summary
 * @returns {Promise<{totalPending: number, oldestPending: Date|null, daysOld: number|null}>}
 */
export const getSyncStatusSummary = async () => {
  try {
    const pendingPatients = await db.patients.where('syncStatus').equals('pending').toArray();
    const pendingReports = await db.reports.where('syncStatus').equals('pending').toArray();
    
    const totalPending = pendingPatients.length + pendingReports.length;
    
    if (totalPending === 0) {
      return {
        totalPending: 0,
        oldestPending: null,
        daysOld: null
      };
    }
    
    // Find oldest pending data
    const allPending = [...pendingPatients, ...pendingReports];
    const oldestPending = allPending.reduce((oldest, current) => {
      const currentDate = new Date(current.createdAt);
      const oldestDate = oldest ? new Date(oldest.createdAt) : new Date();
      return currentDate < oldestDate ? current : oldest;
    });
    
    const daysOld = Math.floor(
      (new Date() - oldestPending.createdAt) / (24 * 60 * 60 * 1000)
    );
    
    return {
      totalPending,
      oldestPending: new Date(oldestPending.createdAt),
      daysOld
    };
  } catch (error) {
    console.error('❌ Error getting sync status summary:', error);
    return {
      totalPending: 0,
      oldestPending: null,
      daysOld: null
    };
  }
};

// Export configuration for easy adjustment
export const SYNC_CONFIG = {
  WARNING_DAYS: SYNC_WARNING_DAYS,
  BLOCK_DAYS: SYNC_BLOCK_DAYS
}; 